/**
 * Integrations Dashboard Component
 * 
 * Allows users to connect to Xero and QuickBooks,
 * sync invoices, and manage accounting software integrations
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import InvoicePreview from '../shared/InvoicePreview';
import { downloadInvoice } from '../../utils/invoiceDownload';
import {
  Link as LinkIcon,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Download,
  Upload,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Building2,
  FileText,
  DollarSign,
  Clock,
  Eye
} from 'lucide-react';

const PROVIDERS = [
  { id: 'xero', name: 'Xero', color: 'bg-green-500' },
  { id: 'quickbooks', name: 'QuickBooks', color: 'bg-blue-500' }
];

function Integrations() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeProvider, setActiveProvider] = useState('xero');
  const [loading, setLoading] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(true); // Track connection loading state
  const [connecting, setConnecting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Connection state
  const [connections, setConnections] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});
  
  // Invoices and sync state
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [accountMappings, setAccountMappings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  // Invoice preview state
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 15
  });
  
  // Filters
  const [filter, setFilter] = useState('all'); // 'all', 'sent', 'received', 'synced'
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  
  // Collapsible top section state
  const [isTopSectionExpanded, setIsTopSectionExpanded] = useState(() => {
    // Check localStorage for user preference
    const saved = localStorage.getItem('integrations-top-section-expanded');
    return saved !== null ? saved === 'true' : true; // Default to expanded
  });
  const [hasUserToggled, setHasUserToggled] = useState(() => {
    // Check if user has manually toggled before
    return localStorage.getItem('integrations-top-section-user-toggled') === 'true';
  });

  // Track auto-collapse timer
  const autoCollapseTimerRef = useRef(null);

  // Handle OAuth callback redirect
  useEffect(() => {
    const connected = searchParams.get('connected');
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'true' && connected) {
      setSuccessMessage(`${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully!`);
      // Reload connections to show updated status
      loadConnections();
      // Clear URL params after 5 seconds
      setTimeout(() => {
        setSearchParams({});
        setSuccessMessage(null);
      }, 5000);
    } else if (error) {
      setErrorMessage(`Connection failed: ${error}`);
      setTimeout(() => {
        setSearchParams({});
        setErrorMessage(null);
      }, 5000);
    }
  }, [searchParams]);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
    loadInvoices();
  }, []);

  // Event-driven updates: Listen for invoice sync/unapproval events
  // This replaces polling - updates happen immediately when invoices are synced/unapproved
  useEffect(() => {
    const handleInvoiceSynced = (event) => {
      console.log('🔄 Invoice synced event received:', event.detail);
      // Refresh invoice list immediately
      loadInvoices(true); // Silent refresh
    };

    // Listen for integration events
    window.addEventListener('invoiceSynced', handleInvoiceSynced);

    // Cleanup
    return () => {
      window.removeEventListener('invoiceSynced', handleInvoiceSynced);
    };
  }, []); // Only run once on mount

  // Refresh invoices when page becomes visible (user navigates back to tab)
  // Only refresh once when tab becomes visible, not continuously
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh once when user returns to tab
        loadInvoices(true); // Silent refresh
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Auto-collapse top section after 6 seconds if user hasn't manually toggled
  useEffect(() => {
    // Clear any existing timer first
    if (autoCollapseTimerRef.current) {
      clearTimeout(autoCollapseTimerRef.current);
      autoCollapseTimerRef.current = null;
    }

    // Skip if user has manually toggled before
    if (hasUserToggled) {
      return;
    }

    // Skip if section is already collapsed
    if (!isTopSectionExpanded) {
      return;
    }

    console.log('⏰ Auto-collapse timer started - will collapse in 6 seconds');
    
    // Set timer to auto-collapse after 6 seconds
    autoCollapseTimerRef.current = setTimeout(() => {
      // Double-check that user hasn't toggled during the 6 seconds
      const stillNotToggled = localStorage.getItem('integrations-top-section-user-toggled') !== 'true';
      const stillExpanded = localStorage.getItem('integrations-top-section-expanded') !== 'false';
      
      if (stillNotToggled && stillExpanded) {
        console.log('⏰ Auto-collapse timer fired - collapsing section');
        setIsTopSectionExpanded(false);
        localStorage.setItem('integrations-top-section-expanded', 'false');
      } else {
        console.log('⏰ Auto-collapse timer fired but state changed - not collapsing');
      }
      autoCollapseTimerRef.current = null;
    }, 6000);

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current);
        autoCollapseTimerRef.current = null;
      }
    };
  }, []); // Run once on mount

  // Save preference to localStorage when user manually toggles
  const handleToggleTopSection = () => {
    const newExpanded = !isTopSectionExpanded;
    setIsTopSectionExpanded(newExpanded);
    setHasUserToggled(true);
    localStorage.setItem('integrations-top-section-expanded', String(newExpanded));
    localStorage.setItem('integrations-top-section-user-toggled', 'true');
  };

  // Load accounts when provider changes and connected
  useEffect(() => {
    if (connectionStatus[activeProvider]?.connected) {
      loadAccounts();
    }
  }, [activeProvider, connectionStatus]);

  // Load connection status for all providers
  const loadConnections = async () => {
    try {
      setLoadingConnections(true);
      const response = await api.get('/integrations/connections');
      console.log('🔍 Load connections response:', JSON.stringify(response.data, null, 2));
      console.log('🔍 Response success?', response.data.success);
      console.log('🔍 Response data array:', response.data.data);
      console.log('🔍 Number of connections:', response.data.data?.length || 0);
      
      if (response.data.success) {
        const connectionsMap = {};
        const statusMap = {};
        
        const connectionsArray = response.data.data || [];
        console.log('🔍 Processing connections array:', connectionsArray);
        
        connectionsArray.forEach(conn => {
          console.log('🔍 Processing connection:', conn.provider, 'is_active:', conn.is_active);
          connectionsMap[conn.provider] = conn;
          statusMap[conn.provider] = {
            connected: conn.is_active,
            accountName: conn.provider_account_name,
            connectedAt: conn.connected_at,
            exists: true // Track if connection exists even if inactive
          };
        });

        console.log('🔍 Final connections map:', connectionsMap);
        console.log('🔍 Final status map:', statusMap);
        setConnections(connectionsMap);
        setConnectionStatus(statusMap);
        
        // Load sync settings and accounts if connected
        Object.keys(statusMap).forEach(provider => {
          if (statusMap[provider].connected) {
            loadSyncSettings(provider);
            // Auto-sync accounts if not already synced
            if (provider === activeProvider && accounts.length === 0) {
              loadAccounts();
            }
          }
        });
      } else {
        console.warn('⚠️ API returned success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ Error loading connections:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error message:', error.message);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Load sync settings
  const loadSyncSettings = async (provider) => {
    try {
      const response = await api.get(`/integrations/${provider}/status`);
      if (response.data.success && response.data.data) {
        // Connection loaded - no auto-sync settings needed
      }
    } catch (error) {
      console.error('Error loading sync settings:', error);
    }
  };

  // Load chart of accounts
  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/integrations/${activeProvider}/accounts`);
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load invoices with sync status
  // silent: if true, refresh without showing loading spinner (for polling)
  const loadInvoices = async (silent = false, page = currentPage) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get(`/integrations/invoices?page=${page}&limit=15`);
      if (response.data.success) {
        const invoicesData = response.data.data.invoices || [];
        setInvoices(invoicesData);
        
        // Always set pagination - either from response or calculate it
        if (response.data.data.pagination) {
          setPagination(response.data.data.pagination);
        } else {
          // Fallback: calculate pagination from response data
          const total = response.data.data.total || invoicesData.length;
          const totalPages = Math.ceil(total / 15);
          setPagination({
            total: total,
            totalPages: totalPages,
            hasNextPage: page * 15 < total,
            hasPrevPage: page > 1,
            limit: 15,
            currentPage: page
          });
        }
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Connect to provider
  const handleConnect = async (provider) => {
    try {
      setConnecting(true);
      const response = await api.get(`/integrations/${provider}/connect`);
      
      if (response.data.success && response.data.data.authorizationUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = response.data.data.authorizationUrl;
      }
    } catch (error) {
      console.error('Error initiating connection:', error);
      alert('Failed to connect. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect from provider
  const handleDisconnect = async (provider) => {
    if (!confirm(`Are you sure you want to disconnect from ${provider}?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.post(`/integrations/${provider}/disconnect`);
      await loadConnections();
      setAccounts([]);
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Failed to disconnect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Save sync settings

  // Sync chart of accounts
  const handleSyncAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.post(`/integrations/${activeProvider}/sync-accounts`);
      if (response.data.success) {
        const previousCount = accounts.length;
        await loadAccounts();
        const newCount = response.data.data?.accountCount || accounts.length;
        const message = previousCount > 0
          ? `Successfully resynced! Found ${newCount} account${newCount !== 1 ? 's' : ''} in ${PROVIDERS.find(p => p.id === activeProvider)?.name}.`
          : `Successfully synced ${newCount} account${newCount !== 1 ? 's' : ''}!`;
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error syncing accounts:', error);
      setErrorMessage('Failed to sync accounts. Please try again.');
      setTimeout(() => setErrorMessage(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Toggle invoice selection
  const toggleInvoiceSelection = (invoiceId) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
    } else {
      newSelected.add(invoiceId);
    }
    setSelectedInvoices(newSelected);
  };

  // Select all invoices
  const selectAllInvoices = () => {
    const filtered = getFilteredInvoices();
    const allSelected = filtered.every(inv => selectedInvoices.has(inv.id));
    
    if (allSelected) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filtered.map(inv => inv.id)));
    }
  };

  // Get filtered invoices
  const getFilteredInvoices = () => {
    return invoices.filter(invoice => {
      if (filter === 'all') return true;
      if (filter === 'sent') return invoice.isSent;
      if (filter === 'received') return !invoice.isSent;
      if (filter === 'synced') {
        return invoice.syncs && invoice.syncs.filter(s => s.provider === activeProvider && s.status === 'synced').length > 0;
      }
      return true;
    });
  };

  const handleSyncInvoice = async (invoiceId, provider, accountMapping = {}) => {
    // Find the invoice to check its status
    const invoice = invoices.find(inv => inv.id === invoiceId);
    
    // Check if invoice is rejected or unapproved
    if (invoice && (invoice.status === 'rejected' || invoice.approvalStatus === 'rejected')) {
      const warningMessage = `⚠️ Warning: Invoice ${invoice.invoiceNumber} is currently rejected.\n\n` +
        `Syncing a rejected invoice may cause issues in your accounting software.\n\n` +
        `Are you sure you want to proceed?`;
      
      if (!confirm(warningMessage)) {
        return; // User cancelled
      }
    }
    
    try {
      setSyncing(true);
      const response = await api.post('/integrations/sync', {
        invoiceId,
        provider: provider || activeProvider,
        accountMapping
      });

      if (response.data.success) {
        // Dispatch event to update UI immediately
        window.dispatchEvent(new CustomEvent('invoiceSynced', {
          detail: {
            invoiceId,
            provider: provider || activeProvider,
            invoiceNumber: invoice?.invoiceNumber
          }
        }));
        
        await loadInvoices();
        alert('Invoice synced successfully!');
      }
    } catch (error) {
      console.error('Error syncing invoice:', error);
      alert(error.response?.data?.message || 'Failed to sync invoice. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Batch sync selected invoices
  const handleBatchSync = async () => {
    if (selectedInvoices.size === 0) {
      alert('Please select at least one invoice to sync.');
      return;
    }

    // Check for rejected/unapproved invoices
    const invoiceIds = Array.from(selectedInvoices);
    const rejectedInvoices = invoices.filter(inv => 
      invoiceIds.includes(inv.id) && 
      (inv.status === 'rejected' || inv.approvalStatus === 'rejected')
    );

    // Show warning if any rejected invoices found
    if (rejectedInvoices.length > 0) {
      const rejectedNumbers = rejectedInvoices.map(inv => inv.invoiceNumber).join(', ');
      const warningMessage = `⚠️ Warning: ${rejectedInvoices.length} invoice(s) are currently rejected:\n\n` +
        `${rejectedNumbers}\n\n` +
        `Syncing rejected invoices may cause issues in your accounting software.\n\n` +
        `Do you want to proceed with syncing all selected invoices?`;
      
      if (!confirm(warningMessage)) {
        return; // User cancelled
      }
    } else {
      // Normal confirmation if no rejected invoices
      if (!confirm(`Sync ${selectedInvoices.size} invoice(s) to ${activeProvider}?`)) {
        return;
      }
    }

    try {
      setSyncing(true);
      const accountMappingsObj = {};
      
      invoiceIds.forEach(id => {
        accountMappingsObj[id] = accountMappings[id] || {};
      });

      const response = await api.post('/integrations/batch-sync', {
        invoiceIds,
        provider: activeProvider,
        accountMappings: accountMappingsObj
      });

      if (response.data.success) {
        const results = response.data.data.results;
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        
        // Dispatch events for each successfully synced invoice
        results.forEach(result => {
          if (result.success) {
            const invoice = invoices.find(inv => inv.id === result.invoiceId);
            window.dispatchEvent(new CustomEvent('invoiceSynced', {
              detail: {
                invoiceId: result.invoiceId,
                provider: activeProvider,
                invoiceNumber: invoice?.invoiceNumber
              }
            }));
          }
        });
        
        alert(`Synced ${successCount} invoice(s) successfully${failCount > 0 ? `. ${failCount} failed.` : ''}`);
        
        await loadInvoices();
        setSelectedInvoices(new Set());
        setAccountMappings({});
      }
    } catch (error) {
      console.error('Error batch syncing:', error);
      alert('Failed to sync invoices. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Update account mapping for invoice
  const updateAccountMapping = (invoiceId, accountCode) => {
    setAccountMappings(prev => ({
      ...prev,
      [invoiceId]: {
        ...prev[invoiceId],
        default: accountCode
      }
    }));
  };

  // Handle invoice preview
  const handlePreviewInvoice = async (invoice) => {
    try {
      // Fetch full invoice data with all details
      const response = await api.get(`/invoices/${invoice.id}`);
      let fullInvoice = response.data.invoice || response.data.data?.invoice || response.data;
      
      // Transform invoice data to match InvoicePreview component expectations
      const transformedInvoice = {
        id: fullInvoice.id,
        invoiceNumber: fullInvoice.invoiceNumber,
        status: fullInvoice.status,
        approvalStatus: fullInvoice.approvalStatus,
        date: fullInvoice.date || fullInvoice.createdAt,
        dueDate: fullInvoice.dueDate,
        currency: fullInvoice.currency || 'GBP',
        total: fullInvoice.total,
        subtotal: fullInvoice.subtotal,
        tax: fullInvoice.tax,
        lineItems: fullInvoice.lineItems || fullInvoice.items || [],
        notes: fullInvoice.notes || '',
        terms: fullInvoice.terms || '',
        poNumber: fullInvoice.poNumber || '',
        createdAt: fullInvoice.createdAt,
        // Sender info (who sent the invoice)
        sender: fullInvoice.sender ? {
          companyName: fullInvoice.sender.companyName,
          firstName: fullInvoice.sender.firstName,
          lastName: fullInvoice.sender.lastName,
          email: fullInvoice.sender.email,
          address: fullInvoice.sender.address,
          city: fullInvoice.sender.city,
          state: fullInvoice.sender.state,
          zipCode: fullInvoice.sender.zipCode,
          country: fullInvoice.sender.country
        } : {
          companyName: user?.companyName,
          firstName: user?.firstName,
          lastName: user?.lastName,
          email: user?.email,
          address: user?.address,
          city: user?.city,
          state: user?.state,
          zipCode: user?.zipCode,
          country: user?.country
        },
        // Receiver info (who received the invoice)
        receiver: fullInvoice.receiver ? {
          companyName: fullInvoice.receiver.companyName,
          firstName: fullInvoice.receiver.firstName,
          lastName: fullInvoice.receiver.lastName,
          email: fullInvoice.receiver.email,
          address: fullInvoice.receiver.address,
          city: fullInvoice.receiver.city,
          state: fullInvoice.receiver.state,
          zipCode: fullInvoice.receiver.zipCode,
          country: fullInvoice.receiver.country
        } : fullInvoice.guestRecipient ? {
          companyName: fullInvoice.guestRecipient.companyName,
          firstName: fullInvoice.guestRecipient.firstName,
          lastName: fullInvoice.guestRecipient.lastName,
          email: fullInvoice.guestRecipient.email,
          address: fullInvoice.guestRecipient.address,
          city: fullInvoice.guestRecipient.city,
          state: fullInvoice.guestRecipient.state,
          zipCode: fullInvoice.guestRecipient.zipCode,
          country: fullInvoice.guestRecipient.country
        } : null,
        isGuest: !fullInvoice.receiverId,
        metadata: fullInvoice.metadata || {}
      };
      
      setPreviewInvoice(transformedInvoice);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching invoice for preview:', error);
      alert('Failed to load invoice details. Please try again.');
    }
  };

  // Handle PDF download for preview
  const handleDownloadPDF = async (invoice) => {
    try {
      await downloadInvoice(invoice, 'pdf', { setLoading });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  // Get sync status for invoice
  const getInvoiceSyncStatus = (invoice) => {
    if (!invoice.syncs || invoice.syncs.length === 0) {
      return { status: 'unsynced', provider: null };
    }
    
    const sync = invoice.syncs.find(s => s.provider === activeProvider);
    if (!sync) {
      return { status: 'unsynced', provider: null };
    }
    
    return { status: sync.status, provider: sync.provider, syncedAt: sync.syncedAt };
  };

  // Calculate sync statistics
  const calculateSyncStats = () => {
    const allInvoices = invoices;
    const synced = allInvoices.filter(inv => {
      const status = getInvoiceSyncStatus(inv);
      return status.status === 'synced';
    }).length;
    const unsynced = allInvoices.filter(inv => {
      const status = getInvoiceSyncStatus(inv);
      return status.status === 'unsynced';
    }).length;
    const failed = allInvoices.filter(inv => {
      const status = getInvoiceSyncStatus(inv);
      return status.status === 'failed';
    }).length;
    
    return { synced, unsynced, failed, total: allInvoices.length };
  };

  const isConnected = connectionStatus[activeProvider]?.connected || false;
  const connectionExists = connectionStatus[activeProvider]?.exists || false;
  const filteredInvoices = getFilteredInvoices();
  const syncStats = calculateSyncStats();
  // Show ALL accounts in dropdown - users should be able to select any account code
  // This ensures all 86 synced accounts are available for selection
  const getAvailableAccounts = (invoice) => {
    // Return all accounts regardless of type - let users choose the right account
    return accounts;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">{successMessage}</span>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{errorMessage}</span>
          </div>
          <button
            onClick={() => setErrorMessage(null)}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Connect and sync invoices with your accounting software
          </p>
          {/* Debug Info */}
          {process.env.NODE_ENV === 'development' && (
            <p className="text-xs text-gray-400 mt-1">
              Connections: {Object.keys(connectionStatus).length} | 
              Active: {Object.keys(connectionStatus).filter(p => connectionStatus[p].connected).length}
            </p>
          )}
        </div>
      </div>

      {/* Provider Tabs with Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="flex items-center border-b border-gray-200">
          {PROVIDERS.map((provider) => (
            <button
              key={provider.id}
              onClick={() => setActiveProvider(provider.id)}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeProvider === provider.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {provider.name}
            </button>
          ))}
          <button
            onClick={handleToggleTopSection}
            className="px-4 py-4 text-gray-400 hover:text-gray-600 transition-colors"
            title={isTopSectionExpanded ? "Hide connection settings" : "Show connection settings"}
          >
            {isTopSectionExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Collapsible Content */}
        {isTopSectionExpanded && (
          <div className="p-6 transition-all duration-300 ease-in-out">
            {/* Connection Status */}
            <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                {loadingConnections ? (
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                    <h3 className="text-lg font-semibold text-gray-500">
                      Loading connection status...
                    </h3>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-2">
                      {isConnected ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-semibold text-gray-900">
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </h3>
                    </div>
                    {isConnected && connectionStatus[activeProvider]?.accountName && (
                      <p className="text-sm text-gray-500 mt-1">
                        Account: {connectionStatus[activeProvider].accountName}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {loadingConnections ? (
                  <div className="px-4 py-2 text-sm text-gray-400">
                    Loading...
                  </div>
                ) : isConnected ? (
                  <button
                    onClick={() => handleDisconnect(activeProvider)}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Disconnect
                  </button>
                ) : connectionExists ? (
                  <>
                    <button
                      onClick={() => handleDisconnect(activeProvider)}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Remove inactive connection"
                    >
                      Remove Connection
                    </button>
                    <button
                      onClick={() => handleConnect(activeProvider)}
                      disabled={connecting || loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <LinkIcon className="w-4 h-4 inline mr-2" />
                          Reconnect
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(activeProvider)}
                    disabled={connecting || loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-4 h-4 inline mr-2" />
                        Connect to {PROVIDERS.find(p => p.id === activeProvider)?.name}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Manual Account Sync (only show if connected) */}
          {!loadingConnections && isConnected && (
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Chart of Accounts</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {accounts.length > 0 
                      ? `${accounts.length} accounts synced. Click "Resync Accounts" to pull new accounts from ${PROVIDERS.find(p => p.id === activeProvider)?.name}.`
                      : 'Sync your chart of accounts to select account codes for invoices'}
                  </p>
                </div>
                <button
                  onClick={handleSyncAccounts}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                  title={accounts.length > 0 ? "Resync accounts from your accounting software to pull any new accounts" : "Sync accounts from your accounting software"}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 inline mr-2" />
                      {accounts.length > 0 ? 'Resync Accounts' : 'Sync Accounts'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Invoice Sync Dashboard */}
      <div>
        {loadingConnections ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              <span className="ml-3 text-gray-500">Loading connection status...</span>
            </div>
          </div>
        ) : isConnected ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Invoice Sync Dashboard</h2>
              {selectedInvoices.size > 0 && (
                <p className="text-sm text-blue-600 mt-1">
                  {selectedInvoices.size} invoice{selectedInvoices.size !== 1 ? 's' : ''} selected for batch sync
                </p>
              )}
            </div>
            
            {selectedInvoices.size > 0 && (
              <button
                onClick={handleBatchSync}
                disabled={syncing}
                className="px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm hover:shadow-md"
              >
                {syncing ? (
                  <>
                    <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                    Syncing {selectedInvoices.size}...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 inline mr-2" />
                    Sync Selected ({selectedInvoices.size})
                  </>
                )}
              </button>
            )}
          </div>

          {/* Sync Status Summary */}
          <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">Total Invoices</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">{syncStats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600 font-medium">Synced</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">{syncStats.synced}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            
            
            {syncStats.failed > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-600 font-medium">Failed</p>
                    <p className="text-2xl font-bold text-red-900 mt-1">{syncStats.failed}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              </div>
            )}
            
            {syncStats.failed === 0 && syncStats.total > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 font-medium">Sync Progress</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {syncStats.total > 0 ? Math.round((syncStats.synced / syncStats.total) * 100) : 0}%
                    </p>
                  </div>
                  <RefreshCw className="w-8 h-8 text-gray-400" />
                </div>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2 mb-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('sent')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filter === 'sent'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Sent
            </button>
            <button
              onClick={() => setFilter('received')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filter === 'received'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Received
            </button>
            <button
              onClick={() => setFilter('synced')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filter === 'synced'
                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Synced {syncStats.synced > 0 && `(${syncStats.synced})`}
            </button>
          </div>

          {/* Select All and Summary */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filteredInvoices.length > 0 && filteredInvoices.every(inv => selectedInvoices.has(inv.id))}
                  onChange={selectAllInvoices}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All ({filteredInvoices.length} {filteredInvoices.length === 1 ? 'invoice' : 'invoices'})
                </span>
              </label>
              {selectedInvoices.size > 0 && (
                <span className="text-sm font-medium text-blue-600">
                  {selectedInvoices.size} selected
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500">
              Showing {filteredInvoices.length} of {pagination.total || syncStats.total} invoices
              {pagination.totalPages > 1 && ` (Page ${currentPage} of ${pagination.totalPages})`}
            </div>
          </div>

              {/* Invoice List */}
              <div className="space-y-3">
                {filteredInvoices.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No invoices found</p>
                  </div>
                ) : (
                  <>
                  {filteredInvoices.map((invoice) => {
                const syncStatus = getInvoiceSyncStatus(invoice);
                const isSelected = selectedInvoices.has(invoice.id);
                const isExpanded = expandedInvoices.has(invoice.id);
                const accountMapping = accountMappings[invoice.id] || {};
                const defaultAccount = accountMapping.default || '';
                
                // Get appropriate accounts based on invoice type (with fallback to all accounts)
                const availableAccounts = getAvailableAccounts(invoice);

                return (
                  <div
                    key={invoice.id}
                    className={`border rounded-lg p-4 ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleInvoiceSelection(invoice.id)}
                          className="mt-1"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 flex-wrap">
                            <span className="font-semibold text-gray-900">
                              {invoice.invoiceNumber}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              invoice.isSent 
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {invoice.isSent ? 'Sent' : 'Received'}
                            </span>
                            
                            {/* Sync Status Badge */}
                            {syncStatus.status === 'synced' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 border border-green-300 flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Synced
                              </span>
                            )}
                            {syncStatus.status === 'unsynced' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 border border-yellow-300 flex items-center">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Not Synced
                              </span>
                            )}
                            {syncStatus.status === 'failed' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 border border-red-300 flex items-center">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </span>
                            )}
                            {syncStatus.status === 'pending' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 border border-gray-300 flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </span>
                            )}
                          </div>
                          
                          <div className="mt-1 text-sm text-gray-500">
                            {invoice.currency} {parseFloat(invoice.total || 0).toFixed(2)} • 
                            Status: {invoice.status} • 
                            Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreviewInvoice(invoice)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                          title="Preview Invoice"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedInvoices);
                            if (newExpanded.has(invoice.id)) {
                              newExpanded.delete(invoice.id);
                            } else {
                              newExpanded.add(invoice.id);
                            }
                            setExpandedInvoices(newExpanded);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded View */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="space-y-3">
                          {/* Account Code Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Account Code
                            </label>
                            <select
                              value={defaultAccount}
                              onChange={(e) => updateAccountMapping(invoice.id, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="">Select Account...</option>
                              {availableAccounts.map((account) => (
                                <option key={account.provider_account_id} value={account.account_code}>
                                  {account.account_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Sync Button */}
                          <div className="flex items-center justify-between">
                            {syncStatus.status === 'synced' ? (
                              <div className="flex items-center text-sm text-green-600">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Synced on {syncStatus.syncedAt ? new Date(syncStatus.syncedAt).toLocaleDateString() : 'N/A'}
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSyncInvoice(invoice.id, activeProvider, accountMapping)}
                                disabled={syncing || !defaultAccount}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                              >
                                {syncing ? (
                                  <>
                                    <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
                                    Syncing...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 inline mr-2" />
                                    Sync Now
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Pagination Controls */}
              {(() => {
                // Calculate pagination values
                const total = pagination.total || filteredInvoices.length || 0;
                const totalPages = pagination.totalPages || Math.ceil(total / 15);
                const showPagination = total > 15 || totalPages > 1;
                
                if (!showPagination) return null;
                
                return (
                  <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                    <div className="text-sm text-gray-700">
                      Showing page {currentPage} of {totalPages} ({total} total invoices)
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          const prevPage = currentPage - 1;
                          setCurrentPage(prevPage);
                          loadInvoices(false, prevPage);
                        }}
                        disabled={!pagination.hasPrevPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm font-medium"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const nextPage = currentPage + 1;
                          setCurrentPage(nextPage);
                          loadInvoices(false, nextPage);
                        }}
                        disabled={!pagination.hasNextPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm font-medium"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()}
                  </>
                )}
              </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Connect to {PROVIDERS.find(p => p.id === activeProvider)?.name} to start syncing your invoices.
              You'll be able to sync invoices, track payment status, and keep your accounting software up to date.
            </p>
          </div>
        )}
      </div>
      
      {/* Invoice Preview Modal */}
      {showPreview && previewInvoice && (
        <InvoicePreview
          invoice={previewInvoice}
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setPreviewInvoice(null);
          }}
          onDownloadPDF={handleDownloadPDF}
          showDownloadButtons={true}
          title="Invoice Preview"
        />
      )}
    </div>
  );
}

export default Integrations;

